/**
 * Contenedor de Toasts
 * Maneja la posición y renderizado de todas las notificaciones
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import Toast from './Toast';

interface ToastContainerProps {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  maxToasts?: number;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ 
  position = 'top-right',
  maxToasts = 5 
}) => {
  const { notifications, hideNotification } = useNotifications();

  // Limitar el número de toasts visibles para evitar saturación
  const visibleNotifications = notifications.slice(-maxToasts);

  const getPositionStyles = (position: string) => {
    const baseStyles = "fixed z-40 pointer-events-none";
    
    switch (position) {
      case 'top-right':
        return `${baseStyles} top-4 right-4 sm:top-6 sm:right-6`;
      case 'top-left':
        return `${baseStyles} top-4 left-4 sm:top-6 sm:left-6`;
      case 'bottom-right':
        return `${baseStyles} bottom-4 right-4 sm:bottom-6 sm:right-6`;
      case 'bottom-left':
        return `${baseStyles} bottom-4 left-4 sm:bottom-6 sm:left-6`;
      case 'top-center':
        return `${baseStyles} top-20 left-1/2 transform -translate-x-1/2 sm:top-24`;
      case 'bottom-center':
        return `${baseStyles} bottom-4 left-1/2 transform -translate-x-1/2 sm:bottom-6`;
      default:
        return `${baseStyles} top-20 right-4 sm:top-24 sm:right-6`;
    }
  };

  if (visibleNotifications.length === 0) {
    return null;
  }

  const toastContainer = (
    <div className={getPositionStyles(position)}>
      <div className="w-full max-w-sm pointer-events-auto">
        {visibleNotifications.map((notification) => (
          <Toast
            key={notification.id}
            notification={notification}
            onClose={hideNotification}
          />
        ))}
      </div>
    </div>
  );

  // Usar portal para renderizar fuera del árbol de componentes
  return createPortal(toastContainer, document.body);
};

export default ToastContainer;